import type { SelectOption } from '@rio-cloud/rio-uikit/Select';
import { FormattedMessage } from 'react-intl';

import type { MaterialType } from '#domain/Material.ts';

export const cargoPackagingTranslationKeys = {
    unspecified: 'intl-msg:ordercom-cloud-frontend.order.packaging-type.unspecified',
    unpackaged: 'intl-msg:ordercom-cloud-frontend.order.packaging-type.unpackaged',
    'euro-pallet': 'intl-msg:ordercom-cloud-frontend.order.packaging-type.euro-pallet',
    'industrial-pallet': 'intl-msg:ordercom-cloud-frontend.order.packaging-type.industrial-pallet',
    'one-way-pallet': 'intl-msg:ordercom-cloud-frontend.order.packaging-type.one-way-pallet',
    'cage-pallet': 'intl-msg:ordercom-cloud-frontend.order.packaging-type.cage-pallet',
    'cardboard-box': 'intl-msg:ordercom-cloud-frontend.order.packaging-type.cardboard-box',
    rack: 'intl-msg:ordercom-cloud-frontend.order.packaging-type.rack',
    'roll-container': 'intl-msg:ordercom-cloud-frontend.order.packaging-type.roll-container',
    'big-bag': 'intl-msg:ordercom-cloud-frontend.order.packaging-type.big-bag',
    ibc: 'intl-msg:ordercom-cloud-frontend.order.packaging-type.ibc',
    coil: 'intl-msg:ordercom-cloud-frontend.order.packaging-type.coil',
    'transport-rack': 'intl-msg:ordercom-cloud-frontend.order.packaging-type.transport-rack',
    trailer: 'intl-msg:ordercom-cloud-frontend.order.packaging-type.trailer',
} as const;

export type CargoPackagingTypes = keyof typeof cargoPackagingTranslationKeys;
export const cargoPackagingTypes = Object.keys(cargoPackagingTranslationKeys) as CargoPackagingTypes[];
export const isCargoPackagingType = (value?: string): value is CargoPackagingTypes =>
    typeof value === 'string' && cargoPackagingTypes.includes(value as CargoPackagingTypes);

export const bulkPackagingTranslationKeys = {
    'bulk-material': 'intl-msg:ordercom-cloud-frontend.order.packaging-type.bulk-material',
} as const;

export type BulkPackagingTypes = keyof typeof bulkPackagingTranslationKeys;
export const bulkPackagingTypes = Object.keys(bulkPackagingTranslationKeys) as BulkPackagingTypes[];
export const isBulkPackagingType = (value?: string): value is BulkPackagingTypes =>
    typeof value === 'string' && bulkPackagingTypes.includes(value as BulkPackagingTypes);

export type PackagingTypes = CargoPackagingTypes | BulkPackagingTypes;

export const packagingTranslationKeys = {
    ...cargoPackagingTranslationKeys,
    ...bulkPackagingTranslationKeys,
};

const headerTranslationKeys = {
    'cargo-space-header': 'intl-msg:ordercom-cloud-frontend.common.cargo-space',
    'load-carrier-header': 'intl-msg:ordercom-cloud-frontend.common.load-carrier',
} as const;

type Headers = keyof typeof headerTranslationKeys;

export type PackagingOption = SelectOption & {
    id: PackagingTypes | Headers;
    type: MaterialType;
};

export const packagingOptions: PackagingOption[] = [
    {
        id: 'unspecified',
        label: <FormattedMessage id={cargoPackagingTranslationKeys.unspecified} />,
        selected: true,
        icon: <span className='uikit-icon icon-question' />,
        type: 'cargo-material',
    },
    {
        id: 'cargo-space-header',
        label: <FormattedMessage id={headerTranslationKeys['cargo-space-header']} />,
        header: true,
        type: 'bulk-material',
    },
    {
        id: 'bulk-material',
        label: <FormattedMessage id={bulkPackagingTranslationKeys['bulk-material']} />,
        icon: <span className='uikit-icon icon-trailer-tipping' />,
        type: 'bulk-material',
    },
    {
        id: 'load-carrier-header',
        label: <FormattedMessage id={headerTranslationKeys['load-carrier-header']} />,
        header: true,
        type: 'cargo-material',
    },
    {
        id: 'unpackaged',
        label: <FormattedMessage id={cargoPackagingTranslationKeys.unpackaged} />,
        icon: <span className='uikit-icon icon-box-open' />,
        type: 'cargo-material',
    },
    {
        id: 'euro-pallet',
        label: <FormattedMessage id={cargoPackagingTranslationKeys['euro-pallet']} />,
        icon: <span className='uikit-icon icon-palette' />,
        type: 'cargo-material',
    },
    {
        id: 'industrial-pallet',
        label: <FormattedMessage id={cargoPackagingTranslationKeys['industrial-pallet']} />,
        icon: <span className='uikit-icon icon-palette' />,
        type: 'cargo-material',
    },
    {
        id: 'one-way-pallet',
        label: <FormattedMessage id={cargoPackagingTranslationKeys['one-way-pallet']} />,
        icon: <span className='uikit-icon icon-palette' />,
        type: 'cargo-material',
    },
    {
        id: 'cage-pallet',
        label: <FormattedMessage id={cargoPackagingTranslationKeys['cage-pallet']} />,
        icon: <span className='uikit-icon icon-mesh-box' />,
        type: 'cargo-material',
    },
    {
        id: 'cardboard-box',
        label: <FormattedMessage id={cargoPackagingTranslationKeys['cardboard-box']} />,
        icon: <span className='uikit-icon icon-box' />,
        type: 'cargo-material',
    },
    {
        id: 'rack',
        label: <FormattedMessage id={cargoPackagingTranslationKeys.rack} />,
        icon: <span className='uikit-icon icon-shelve' />,
        type: 'cargo-material',
    },
    {
        id: 'roll-container',
        label: <FormattedMessage id={cargoPackagingTranslationKeys['roll-container']} />,
        icon: <span className='uikit-icon icon-roll-container' />,
        type: 'cargo-material',
    },
    {
        id: 'big-bag',
        label: <FormattedMessage id={cargoPackagingTranslationKeys['big-bag']} />,
        icon: <span className='uikit-icon icon-big-bag' />,
        type: 'cargo-material',
    },
    {
        id: 'ibc',
        label: <FormattedMessage id={cargoPackagingTranslationKeys.ibc} />,
        icon: <span className='uikit-icon icon-bulk-container' />,
        type: 'cargo-material',
    },
    {
        id: 'coil',
        label: <FormattedMessage id={cargoPackagingTranslationKeys.coil} />,
        icon: <span className='uikit-icon icon-coil' />,
        type: 'cargo-material',
    },
    {
        id: 'transport-rack',
        label: <FormattedMessage id={cargoPackagingTranslationKeys['transport-rack']} />,
        icon: <span className='uikit-icon icon-stillage' />,
        type: 'cargo-material',
    },
];
